import React, { useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { Button } from '../ui/button.tsx';
import { BaseTable } from '../ui/base-table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown.tsx';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar.tsx';
import { Badge } from '../ui/badge.tsx';
import { MoreHorizontal, Plus, Search, SlidersHorizontal } from 'lucide-react';
import { useGetParents, useDeleteParent } from '../../api/parent/parentQueries';
import { toast } from 'react-toastify';
import { ParentEditPanel } from './ParentEditPanel';
import { cn } from '../../lib/utils';
import { Input } from '../ui/input.tsx';
import { InlineFilter } from '../ui/inline-filter.tsx';
import { motion } from 'framer-motion';

const columns = [
  {
    key: 'name',
    label: 'NAME',
    render: (parent) => (
      <div className="flex items-center gap-3">
        <Avatar className="h-9 w-9 border border-gray-100">
          <AvatarImage src={parent.avatar} alt={parent.name} />
          <AvatarFallback className="bg-purple-50 text-purple-600 font-medium">
            {parent.name?.split(' ').map(n => n[0]).join('')}
          </AvatarFallback>
        </Avatar>
        <div className="min-w-[180px]">
          <div className="font-medium text-gray-900">{parent.name}</div>
          <div className="text-sm text-gray-500 truncate">{parent.email}</div>
        </div>
      </div>
    ),
  },
  {
    key: 'phone',
    label: 'PHONE',
    render: (parent) => (
      <div className="text-sm text-gray-600">{parent.phone}</div>
    ),
  },
  {
    key: 'children',
    label: 'CHILDREN',
    render: (parent) => (
      <div className="text-sm font-medium text-gray-900">
        {parent.children.length} {parent.children.length === 1 ? 'child' : 'children'}
      </div>
    ),
  },
  {
    key: 'registeredDate',
    label: 'REGISTERED',
    render: (parent) => (
      <div className="text-sm text-gray-600">
        {new Date(parent.registeredDate).toLocaleDateString('en-GB', {
          day: '2-digit',
          month: 'short',
          year: 'numeric'
        })}
      </div>
    ),
  },
  {
    key: 'status',
    label: 'STATUS',
    render: (parent) => (
      <Badge
        variant="outline"
        className={`px-2.5 py-0.5 text-xs font-medium ${
          parent.isActive 
            ? 'bg-green-50 text-green-700 border-green-200' 
            : 'bg-red-50 text-red-700 border-red-200'
        }`}
      >
        {parent.isActive ? 'Active' : 'Inactive'}
      </Badge>
    ),
  },
];

export function ParentTable() {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selectedParent, setSelectedParent] = useState(null);
  const [isEditPanelOpen, setIsEditPanelOpen] = useState(false);
  const [localSearchQuery, setLocalSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    status: {
      active: false,
      inactive: false
    },
    children: {
      hasChildren: false,
      noChildren: false
    }
  });

  const handleResetFilters = () => {
    setFilters({
      status: {
        active: false,
        inactive: false
      },
      children: {
        hasChildren: false,
        noChildren: false
      }
    });
  };

  const filterGroups = [
    {
      id: 'status',
      label: 'Status',
      options: [
        { id: 'active', label: 'Active', checked: filters.status.active },
        { id: 'inactive', label: 'Inactive', checked: filters.status.inactive }
      ]
    },
    {
      id: 'children',
      label: 'Children',
      options: [
        { id: 'hasChildren', label: 'Has Children', checked: filters.children.hasChildren },
        { id: 'noChildren', label: 'No Children', checked: filters.children.noChildren }
      ]
    }
  ];

  const handleFilterChange = (groupId, optionId, checked) => {
    setFilters(prev => ({
      ...prev,
      [groupId]: {
        ...prev[groupId],
        [optionId]: checked
      }
    }));
  };

  const context = useOutletContext() || {};
  const { searchQuery = '', setSearchQuery } = context;

  const { data, isLoading, error } = useGetParents();
  const { mutate: deleteParent, isPending: isDeleting } = useDeleteParent();

  const parents = Array.isArray(data) ? data : [];

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setLocalSearchQuery(value);
    if (setSearchQuery) {
      setSearchQuery(value);
    }
  };

  const effectiveSearchQuery = searchQuery || localSearchQuery;

  const filteredParents = parents.filter(parent => {
    if (effectiveSearchQuery) {
      const searchQuery = effectiveSearchQuery.toLowerCase();
      const fullName = `${parent.firstName} ${parent.lastName}`.toLowerCase();
      
      const matchesSearch = 
        fullName.includes(searchQuery) ||
        parent.email?.toLowerCase().includes(searchQuery) ||
        parent.phone?.toString().includes(searchQuery) ||
        parent.children?.some(child => 
          `${child.firstName} ${child.lastName}`.toLowerCase().includes(searchQuery)
        );

      if (!matchesSearch) return false;
    }

    if (filters.status.active || filters.status.inactive) {
      if (filters.status.active && !parent.isActive) return false;
      if (filters.status.inactive && parent.isActive) return false;
    }

    if (filters.children.hasChildren || filters.children.noChildren) {
      const hasChildren = parent.children?.length > 0;
      if (filters.children.hasChildren && !hasChildren) return false;
      if (filters.children.noChildren && hasChildren) return false;
    }

    return true;
  });

  const handleRowClick = (parent) => {
    setSelectedParent(parent);
    setIsEditPanelOpen(true);
  };

  const handleEdit = (parent, e) => {
    e?.stopPropagation();
    setSelectedParent(parent);
    setIsEditPanelOpen(true);
  };

  const handleDelete = async (parent, e) => {
    e.stopPropagation();
    if (window.confirm('Are you sure you want to delete this parent?')) {
      try {
        await deleteParent(parent.id);
        toast.success('Parent deleted successfully');
      } catch (error) {
        toast.error('Failed to delete parent');
      }
    }
  };

  const rowActions = (parent) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
        <Button variant="ghost" size="icon" className="h-8 w-8" disabled={isDeleting}>
          <MoreHorizontal className="h-4 w-4" />
          <span className="sr-only">Open menu</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[160px]">
        <DropdownMenuItem 
          onClick={(e) => handleEdit(parent, e)}
          className="text-gray-700"
        >
          Edit
        </DropdownMenuItem>
        <DropdownMenuItem
          className="text-red-600"
          onClick={(e) => handleDelete(parent, e)}
          disabled={isDeleting}
        >
          {isDeleting ? 'Deleting...' : 'Delete'}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedData = filteredParents.slice(startIndex, endIndex);

  if (error) {
    return (
      <div className="text-red-500">Failed to load parents: {error.message}</div>
    );
  }

  return (
    <>
      <div className="space-y-6 p-6">
        <div className="flex items-center justify-between">
          <motion.div 
            className="flex items-center gap-4 flex-1 max-w-[600px]"
            layout
          >
            <div className="flex-1 flex items-center gap-4 min-w-0">
              <InlineFilter 
                groups={filterGroups}
                onFilterChange={handleFilterChange}
                onReset={handleResetFilters}
              />
              <div className="relative flex-1 min-w-[200px] max-w-[300px]">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by name, email or phone"
                  className="w-full pl-8"
                  value={effectiveSearchQuery}
                  onChange={handleSearchChange}
                />
              </div>
            </div>
          </motion.div>
          <Button
            className="bg-purple-600 hover:bg-purple-700 text-white shrink-0"
            onClick={() => navigate('/parent/new')}
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Parent
          </Button>
        </div>

        <div className="rounded-lg border border-gray-200 bg-white">
          <BaseTable
            columns={columns}
            data={paginatedData}
            rowActions={rowActions}
            onRowClick={handleRowClick}
            pageSize={pageSize}
            currentPage={currentPage}
            totalItems={filteredParents.length}
            onPageChange={setCurrentPage}
            onPageSizeChange={setPageSize}
            isLoading={isLoading}
          />
        </div>
      </div>

      <ParentEditPanel 
        parent={selectedParent}
        isOpen={isEditPanelOpen}
        onClose={() => {
          setIsEditPanelOpen(false);
          setSelectedParent(null);
        }}
      />
    </>
  );
}